import type {
  ClaimOnboardingCelebrationResponseDto,
  OrganizationLogoResponseDto,
  OrganizationLogoSmallResponseDto,
  OrganizationResponseDto,
  UpdateOrganizationBusinessDto,
  UpdateOrganizationInvoicingDto,
  UpdateOrganizationPaymentOptionsDto,
  UpdateOrganizationTaxDto,
} from '@trellis/api'
import type {
  RequestResult,
  SetNextInvoiceNumber,
  UpdateOrganizationAccess,
  UpdateOrganizationBusiness,
  UpdateOrganizationInvoicing,
  UpdateOrganizationPaymentOptions,
  UpdateOrganizationTax,
  UpdateOrganizationTerms,
} from '@trellis/shared'

import { env } from '@/env'
import { apiClient } from '@/lib/api-client'
import { request } from '@/lib/request'
import { getStoredTenantSlug } from '@/lib/tenant'

export const getOrganization = () =>
  request<OrganizationResponseDto | null>(() =>
    apiClient.api.organizationControllerGetOrganization(),
  )

export const updateOrganizationBusiness = (data: UpdateOrganizationBusiness) =>
  request<OrganizationResponseDto>(() =>
    // Cast: generated DTO marks optional fields as required; runtime values are valid.
    apiClient.api.organizationControllerUpdateBusiness(
      data as UpdateOrganizationBusinessDto,
    ),
  )

export const updateOrganizationTax = (data: UpdateOrganizationTax) =>
  request<OrganizationResponseDto>(() =>
    // Cast: generated DTO marks optional fields as required; runtime values are valid.
    apiClient.api.organizationControllerUpdateTax(data as UpdateOrganizationTaxDto),
  )

export const acceptOrganizationTerms = (data: UpdateOrganizationTerms) =>
  request<OrganizationResponseDto>(() =>
    apiClient.api.organizationControllerAcceptTerms(data),
  )

export const updateOrganizationInvoicing = (data: UpdateOrganizationInvoicing) =>
  request<OrganizationResponseDto>(() =>
    apiClient.api.organizationControllerUpdateInvoicing(
      data as UpdateOrganizationInvoicingDto,
    ),
  )

export const claimOnboardingCelebration = () =>
  request<ClaimOnboardingCelebrationResponseDto>(() =>
    apiClient.api.organizationControllerClaimOnboardingCelebration(),
  )

export const setNextInvoiceNumber = (data: SetNextInvoiceNumber) =>
  request<OrganizationResponseDto>(() =>
    apiClient.api.organizationControllerSetNextInvoiceNumber(data),
  )

export const updateOrganizationPaymentOptions = (
  data: UpdateOrganizationPaymentOptions,
) =>
  request<OrganizationResponseDto>(() =>
    // Cast: generated DTO marks optional fields as required; runtime values are valid.
    apiClient.api.organizationControllerUpdatePaymentOptions(
      data as UpdateOrganizationPaymentOptionsDto,
    ),
  )

async function uploadBrandingLogo<T>(
  endpointPath: string,
  file: File,
): Promise<RequestResult<T>> {
  const formData = new FormData()
  formData.append('file', file)

  try {
    const tenantSlug = getStoredTenantSlug()
    const response = await fetch(`${env.VITE_API_BASE_URL}${endpointPath}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : undefined,
    })
    const body = await response.json()
    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: { code: body.code, message: body.message },
      }
    }
    return { status: response.status, data: body.data, pagination: null, error: null }
  } catch (caughtError) {
    return {
      status: 500,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error ? caughtError.message : 'Logo upload failed',
      },
    }
  }
}

export const uploadOrganizationLogo = (file: File) =>
  uploadBrandingLogo<OrganizationLogoResponseDto>('/api/organization/branding/logo', file)

export const deleteOrganizationLogo = () =>
  request<void>(() => apiClient.api.organizationControllerDeleteLogo())

export const uploadOrganizationLogoSmall = (file: File) =>
  uploadBrandingLogo<OrganizationLogoSmallResponseDto>(
    '/api/organization/branding/logo-small',
    file,
  )

export const deleteOrganizationLogoSmall = () =>
  request<void>(() => apiClient.api.organizationControllerDeleteLogoSmall())

export type BrandingPreviewType = 'invoice' | 'invoice-email' | 'quote' | 'quote-email'

export const getBrandingPreview = async (
  type: BrandingPreviewType,
  { logo, removeLogo }: { logo: File | null; removeLogo: boolean },
): Promise<RequestResult<string>> => {
  const formData = new FormData()
  if (logo) formData.append('logo', logo)
  if (removeLogo) formData.append('removeLogo', 'true')

  try {
    const tenantSlug = getStoredTenantSlug()
    const response = await fetch(
      `${env.VITE_API_BASE_URL}/api/organization/branding/preview/${type}`,
      {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : undefined,
      },
    )
    if (!response.ok) {
      const body = await response.json()
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: {
          code: body.code ?? 'PREVIEW_ERROR',
          message: body.message ?? 'Failed to load branding preview',
        },
      }
    }
    const html = await response.text()
    return { status: response.status, data: html, pagination: null, error: null }
  } catch (caughtError) {
    return {
      status: 0,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to connect to the server',
      },
    }
  }
}

export const updateOrganizationAccess = async (
  data: UpdateOrganizationAccess,
): Promise<RequestResult<OrganizationResponseDto>> => {
  try {
    const tenantSlug = getStoredTenantSlug()
    const response = await fetch(`${env.VITE_API_BASE_URL}/api/organization/access`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {}),
      },
      body: JSON.stringify(data),
    })
    const body = await response.json()
    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: {
          code: body.code ?? 'API_ERROR',
          message: body.message ?? 'Failed to update access settings',
        },
      }
    }
    return { status: response.status, data: body.data, pagination: null, error: null }
  } catch (caughtError) {
    return {
      status: 0,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to connect to the server',
      },
    }
  }
}

export const getMyIp = async (): Promise<RequestResult<{ ip: string }>> => {
  try {
    const response = await fetch(`${env.VITE_API_BASE_URL}/api/auth/my-ip`, {
      credentials: 'include',
    })
    const body = await response.json()
    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: {
          code: body.code ?? 'API_ERROR',
          message: body.message ?? 'Request failed',
        },
      }
    }
    return { status: response.status, data: body.data, pagination: null, error: null }
  } catch (caughtError) {
    return {
      status: 0,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to connect to the server',
      },
    }
  }
}
